import React, { ReactElement, useCallback } from 'react';
import {
  Box, Flex, HStack, keyframes, Link, Menu, MenuButton, MenuItem, MenuList, Portal, Text, Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiEye, FiEyeOff, FiMoreVertical, FiRefreshCw, FiRepeat, FiEdit3,
} from 'react-icons/fi';
import {
  CloseIcon, EditIcon, RepeatIcon, StarIcon,
} from '@chakra-ui/icons';
import { Link as ReactLink, useHistory, useLocation } from 'react-router-dom';
import { NIL } from 'uuid';
import { Project, ProjectStatus } from '../../models/project';
import useProject from '../../hooks/useProject';
import useCurrentUser from '../../hooks/useCurrentUser'; // item 1332 — admin-only hide/show
import { useAppearance } from '../../theme/AppearanceProvider';

// item 322f — extracted from the 1,450-line SidebarWithHeader: one nav/tree row
// (top-level nav item OR a dense project row under a customer group).
interface NavButtonProps {
  icon?: ReactElement<any, any>;
  name: string;
  // item 696 — link is optional: with no link the row is a pure ACTION button (runs onClick),
  // e.g. "My Environment" (launches tabs) living in the sidebar alongside real routes.
  link?: string;
  onClick?: () => void;
  menuObject?: Project;
  onClose?: () => void;
  collapsed?: boolean;
  // item 322f — dense tree rows (projects under a customer): mixed-case name +
  // smaller icon chip. UPPERCASE + letter-spacing costs ~20% width, which is why
  // "Palms Casino Resort" was kissing the sidebar edge; the loud treatment stays
  // for top-level nav labels only.
  dense?: boolean;
  // item 454 — the project has a live subscription: Aurora gets a breathing green
  // glow on the icon chip + a slow-spinning ⟳ marker; Accountant gets the plain
  // static marker only (zero decoration, per the skin rule).
  subscription?: boolean;
  // item 482 — an iPhone-style status badge (a count pill / green check): corner dot on
  // the collapsed rail, right-aligned when expanded.
  badge?: React.ReactNode;
  // item 1110 (Chad) — this project sold a subscription that nobody has enrolled yet. Same ⟳
  // glyph as `subscription`, in ORANGE and not spinning: green-and-turning = billing, orange-
  // and-still = sold but idle. It replaces the global "N not enrolled" pill that used to sit in
  // the KPI strip, which nagged without ever saying WHICH project needed the work.
  enrollmentPending?: boolean;
  // item 1432 (Chad) — N contracts sent for signature at SignWell and not yet signed
  signaturePending?: number;
}

// item 454 — subscription markers. Slow on purpose: alive, not busy.
// PERF (item 406's rule): never animate box-shadow — it re-rasterizes every frame
// per row. The breathe is an OPACITY pulse on an ::after that carries a static
// shadow (compositor-only), and both animations gate on the effects tier.
const subSpin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const subBreathe = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
`;
const NavButton: React.FC<NavButtonProps> = ({
  icon,
  name,
  link,
  onClick,
  menuObject,
  onClose,
  collapsed,
  dense,
  subscription,
  badge,
  enrollmentPending,
  signaturePending = 0,
  ...rest
}) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { aurora, motion: effectsMotion } = useAppearance();
  const {
    setProjectClosed, setProjectReopened, setProjectFavorite, setProjectAdministrative,
  } = useProject({ id: NIL });
  const { isAdmin } = useCurrentUser(); // item 1332
  const linkLower = (link ?? '').toLowerCase();
  const currentPath = pathname.toLowerCase();
  // '/' (dashboard) must match exactly — otherwise it'd match every route
  // item 700 — a link-less ACTION row (e.g. My Environment) is NEVER active (empty link would
  // otherwise startsWith('/') and highlight every render).
  let isActive = false;
  if (!link) isActive = false;
  else if (linkLower === '/') isActive = currentPath === '/';
  else isActive = currentPath === linkLower || currentPath.startsWith(`${linkLower}/`);
  const activeBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const activeColor = useColorModeValue('brandGreen.600', 'brandGreen.300');
  const hoverBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.200');
  const labelColor = useColorModeValue('gray.700', 'whiteAlpha.800');
  // item 1203 — dense tree rows (projects) sit one step dimmer than their customer
  // header so parent/child hierarchy reads at a glance; the active row still lights up.
  const denseLabelColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  const idleLabelColor = dense ? denseLabelColor : labelColor;
  const chipBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const chipActiveBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');
  const menuBgGlass = useColorModeValue('white', 'gray.800');
  const menuBorderGlass = useColorModeValue('gray.200', 'whiteAlpha.300');
  const menuBg = aurora ? menuBgGlass : 'ledgerSurface';
  const menuBorder = aurora ? menuBorderGlass : 'ledgerStroke';
  const handleClick = useCallback(() => {
    if (onClick !== undefined) onClick();
  }, [onClick]);
  // item 696 — route rows are a ReactLink (to=…); link-less action rows are a <button>
  const linkProps: {
    to?: string;
    type?: 'button';
  } = {};
  if (link) linkProps.to = link;
  else linkProps.type = 'button';
  // item 402 — minWidth 0 lets the flex chain SHRINK so the name ellipsizes. A link-less
  // action row also resets the native <button> chrome so it matches the link rows.
  const linkStyle: React.CSSProperties = {
    textDecoration: 'none',
    width: collapsed ? undefined : '100%',
    minWidth: 0,
  };
  if (!link) {
    linkStyle.background = 'none';
    linkStyle.border = '0';
    linkStyle.cursor = 'pointer';
    linkStyle.padding = '0';
    linkStyle.textAlign = 'left';
  }

  const closeMenu = () => {
    if (onClose === undefined) return;
    onClose();
  };

  // item 1110b (Chad: "can the side bar also hover so it tells you to enroll") — the row tooltip
  // was NAME-only and switched off whenever the sidebar is expanded, so the only thing you could
  // hover for an explanation was the 12px ⟳ itself. A pending enrollment now turns the WHOLE ROW
  // into the explanation — and it says what to DO, not just what's wrong, because clicking the
  // row is the actual next step (it lands on the project's Enroll callout, item 737).
  const sigLabel = signaturePending > 0
    ? `${signaturePending} contract${signaturePending === 1 ? '' : 's'} awaiting signature` : ''; // item 1432
  const hoverLabel = (() => {
    if (!enrollmentPending && !signaturePending) return name.toUpperCase();
    if (collapsed) {
      return `${name.toUpperCase()}${enrollmentPending ? ' — subscription sold, click to enroll' : ''}${sigLabel ? ` — ${sigLabel}` : ''}`;
    }
    return [enrollmentPending ? 'Subscription sold — click to enroll' : '', sigLabel].filter(Boolean).join(' · ');
  })();

  return (
    <Tooltip
      label={hoverLabel}
      placement="right"
      hasArrow
      isDisabled={!collapsed && !enrollmentPending && !signaturePending}
    >
      <HStack
        position="relative"
        mx={2}
        my={0.5}
        px={2}
        py={1.5}
        borderRadius="xl"
        role="group"
        bg={isActive ? activeBg : undefined}
        justifyContent={collapsed ? 'center' : 'flex-start'}
        transition="all 0.2s cubic-bezier(0.22, 1, 0.36, 1)"
        _hover={{
          bg: isActive ? activeBg : hoverBg,
          transform: collapsed ? undefined : 'translateX(2px)',
        }}
        {...rest}
      >
        {isActive && !collapsed && (
          <Box
            position="absolute"
            left="-2px"
            top="6px"
            bottom="6px"
            w="3px"
            borderRadius="full"
            bg={aurora ? undefined : 'brandGreen.400'}
            bgGradient={aurora ? 'linear(to-b, brandGreen.300, brandGreen.500)' : undefined}
            boxShadow={aurora ? '0 0 12px var(--chakra-colors-brandGreen-400)' : 'none'}
          />
        )}
        {menuObject && !collapsed
        && (
          <Menu>
            <MenuButton
              fontSize="sm"
              // item 406 — hover-bolding shifted layout (the row wiggled); color-only
              // item 1203 — the ⋮ read as a bullet and added noise: it now sits quiet
              // until the row is hovered (the row is role="group"); a favorite's star
              // stays at full strength — it's a state marker, not an affordance.
              opacity={menuObject.isFavorite ? 1 : 0.35}
              transition="opacity 0.15s ease"
              _groupHover={{ opacity: 1 }}
              _hover={{ color: 'brand.100' }}
            >
              {menuObject.isFavorite
              && <StarIcon />}
              {!menuObject.isFavorite
              && <FiMoreVertical />}
            </MenuButton>
            {/* Portal + solid bg + high zIndex (item 97): the sidebar's
                backdrop-filter made an in-panel MenuList render see-through and
                overlap the rows behind it; portaling it to <body> escapes that
                stacking/blur context and floats it above everything. */}
            <Portal>
              <MenuList fontSize="sm" bg={menuBg} borderColor={menuBorder} boxShadow="xl" zIndex={1600}>
                <MenuItem
                  icon={<StarIcon />}
                  onClick={() => {
                    setProjectFavorite(menuObject.id);
                    closeMenu();
                  }}
                >
                  {menuObject.isFavorite ? 'UNFAVORITE' : 'FAVORITE'}
                </MenuItem>
                <MenuItem
                  icon={<EditIcon />}
                  onClick={() => {
                    history.push(`/project/edit/${menuObject.id}`);
                    closeMenu();
                  }}
                >
                  EDIT
                </MenuItem>
                {menuObject.status === ProjectStatus.Closed ? (
                  <MenuItem
                    icon={<RepeatIcon />}
                    onClick={() => {
                      setProjectReopened(menuObject.id);
                      closeMenu();
                    }}
                  >
                    REOPEN
                  </MenuItem>
                ) : (
                  <MenuItem
                    icon={<CloseIcon />}
                    onClick={() => {
                      setProjectClosed(menuObject.id);
                      closeMenu();
                    }}
                  >
                    CLOSE
                  </MenuItem>
                )}
                {/* item 1166/1332 (Chad: "Hide from project sidebar") — admin-only. The flag was
                    set straight in Azure until now (no control existed). A project under a HIDDEN
                    customer can't be shown on its own — say so instead of offering a no-op. */}
                {isAdmin && (
                  menuObject.customerIsAdministrative ? (
                    <MenuItem icon={<FiEyeOff />} isDisabled>
                      HIDDEN VIA CUSTOMER
                    </MenuItem>
                  ) : (
                    <MenuItem
                      icon={menuObject.isAdministrative ? <FiEye /> : <FiEyeOff />}
                      onClick={() => {
                        setProjectAdministrative(menuObject.id, !menuObject.isAdministrative);
                        // item 1332b — hiding drops the sidebar's transient "Show all" peek so it vanishes now
                        if (!menuObject.isAdministrative) window.dispatchEvent(new CustomEvent('basix:sidebar-hidden'));
                        closeMenu();
                      }}
                    >
                      {menuObject.isAdministrative ? 'SHOW IN PROJECT SIDEBAR' : 'HIDE FROM PROJECT SIDEBAR'}
                    </MenuItem>
                  )
                )}
              </MenuList>
            </Portal>
          </Menu>
        )}
        <Link
          fontSize="xs"
          // item 696 — a real route renders as a ReactLink; a link-less action row renders as a
          // <button> (same styling) that just runs onClick (e.g. "My Environment" launches tabs).
          as={link ? ReactLink : 'button'}
          {...linkProps}
          style={linkStyle}
          onClick={handleClick}
          // open-in-new-tab is now SIDEBAR-ONLY, on DOUBLE-CLICK (item 90). It's a
          // real <a>, so Cmd/Ctrl-click still opens a new tab natively too. The new
          // tab shares the Auth0 session (localStorage cache) — no second login.
          onDoubleClick={(e: React.MouseEvent) => {
            if (!link) return;
            e.preventDefault();
            window.open(link, '_blank', 'noopener');
          }}
          textTransform={dense ? undefined : 'uppercase'}
          sx={{ '&:focus': { boxShadow: 'none' }, '&:focus-visible': { boxShadow: 'outline' } }}
        >
          <HStack spacing={dense ? 2.5 : 3} justify={collapsed ? 'center' : 'flex-start'} minW={0}>
            {/* item 596 — icon is optional: expanded project rows render name-only (Chad) */}
            {icon && (
            <Flex
              w={dense ? '28px' : '36px'}
              h={dense ? '28px' : '36px'}
              flexShrink={0}
              align="center"
              justify="center"
              borderRadius="lg"
              fontSize={dense ? '15px' : '20px'}
              position="relative"
              color={subscription || isActive ? activeColor : labelColor}
              bg={isActive ? chipActiveBg : chipBg}
              boxShadow={isActive && aurora ? '0 0 14px -2px var(--chakra-colors-brandGreen-400)' : undefined}
              // item 454 — subscription projects breathe a soft green glow (Aurora only):
              // static shadow on ::after, opacity pulses (compositor-only, cheap)
              sx={subscription && aurora ? {
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 'lg',
                  pointerEvents: 'none',
                  boxShadow: '0 0 12px -1px var(--chakra-colors-brandGreen-400)',
                  animation: effectsMotion ? `${subBreathe} 3.5s ease-in-out infinite` : undefined,
                  opacity: effectsMotion ? undefined : 0.6,
                },
              } : undefined}
              transition="all 0.2s"
            >
              {icon}
              {/* item 488 — iPhone-style: the status badge (count pill / caught-up check) sits
                  on the icon's TOP-RIGHT corner, so it never collides with the row's pin. */}
              {badge && (
                <Box position="absolute" top="-5px" right="-6px" pointerEvents="none" zIndex={2}>
                  {badge}
                </Box>
              )}
            </Flex>
            )}
            {!collapsed && (
              // item 322f — long project names used to wrap onto extra lines; truncate
              // with an ellipsis and show the full name on hover (the row Tooltip only
              // covers the collapsed rail)
              <Text
                // item 1203 — one step smaller + dimmer than the customer header (13px vs 14px)
                fontSize={dense ? '0.8125rem' : 'xs'}
                fontWeight={dense ? 'medium' : 'semibold'}
                letterSpacing={dense ? 'normal' : '0.06em'}
                color={isActive ? activeColor : idleLabelColor}
                isTruncated
                minW={0}
                title={name}
              >
                {name}
              </Text>
            )}
            {/* item 1171 (Chad: "the animated recurrences are not really in alignment") —
                the name soaks up the slack so every row's marks sit in one right-hand
                column instead of trailing each name at a ragged x. */}
            {!collapsed && <Box flex="1" minW={0} />}
            {/* item 1110 — sold-but-not-enrolled: the same mark, orange and STILL. It sits
                before the live-subscription mark so a project that has both reads
                "one running, one waiting". */}
            {/* item 1432 — awaiting signature: an orange pen, before the enrollment/subscription marks */}
            {signaturePending > 0 && !collapsed && (
              <Box
                as={FiEdit3}
                aria-label={sigLabel}
                flexShrink={0}
                fontSize="12px"
                color={aurora ? 'orange.300' : 'ledgerInk'}
              />
            )}
            {enrollmentPending && !collapsed && (
              /* no tooltip of its own — the whole ROW carries the "click to enroll" hover */
              <Box
                as={FiRepeat}
                aria-label="Subscription sold, not enrolled yet"
                flexShrink={0}
                fontSize="12px"
                color={aurora ? 'orange.300' : 'ledgerInk'}
              />
            )}
            {/* item 454 — subscription marker: spins slowly in Aurora, static in Accountant */}
            {subscription && !collapsed && (
              <Box
                as={FiRefreshCw}
                aria-label="Has subscription"
                flexShrink={0}
                fontSize="12px"
                color={aurora ? 'brandGreen.400' : 'ledgerInk'}
                animation={aurora && effectsMotion ? `${subSpin} 6s linear infinite` : undefined}
              />
            )}
          </HStack>
        </Link>
      </HStack>
    </Tooltip>
  );
};
NavButton.defaultProps = {
  icon: undefined,
  link: undefined,
  onClick: undefined,
  menuObject: undefined,
  onClose: undefined,
  collapsed: false,
  dense: false,
  subscription: false,
  badge: undefined,
  enrollmentPending: false,
  signaturePending: 0,
};

export default NavButton;
